package com.sweetlime.bingeamigo.presentation.features.home

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.ActivityUserContext
import com.sweetlime.bingeamigo.model.NextEpisodeCardState
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.types.ActivityType
import com.sweetlime.bingeamigo.presentation.components.ContinueWatchingRail
import com.sweetlime.bingeamigo.presentation.components.FriendActivityCard
import com.sweetlime.bingeamigo.presentation.components.HeroUpNextCard
import com.sweetlime.bingeamigo.presentation.ui.AccentSystem
import com.sweetlime.bingeamigo.presentation.ui.AppAccents
import com.sweetlime.bingeamigo.state.HomeUiState

@Composable
fun HomeBody(
    uiState: HomeUiState,
    accentSystem: AccentSystem = AccentSystem.Balanced,
    onTvShowClick: (TvShow) -> Unit,
    onMarkNextEpisodeWatched: (tvShowId: Int) -> Unit,
    onActivityClick: (UserActivity) -> Unit,
    onReact: (UserActivity) -> Unit,
    onComment: (UserActivity) -> Unit,
    onAddToList: (UserActivity) -> Unit,
) {
    val accent = remember(accentSystem) { AppAccents.roles(accentSystem) }
    val cards = uiState.nextEpisodeCards
    val groupedActivity = remember(uiState.followingActivity) { groupEpisodeActivity(uiState.followingActivity) }

    var featuredId by remember(cards) { mutableStateOf(cards.firstOrNull()?.tvShow?.id ?: -1) }
    val featured = cards.firstOrNull { it.tvShow.id == featuredId } ?: cards.firstOrNull()

    val isNew: (NextEpisodeCardState) -> Boolean = { it.episodeNumber == 1 || it.watchedThisSeason == 0 }

    LazyColumn(
        modifier = Modifier.fillMaxSize().background(Color(0xFF111113)),
        verticalArrangement = Arrangement.spacedBy(24.dp),
        contentPadding = PaddingValues(top = 4.dp, bottom = 100.dp),
    ) {
        item {
            Row(
                Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                verticalAlignment = Alignment.Bottom,
            ) {
                Text("Evening, ${uiState.username}", color = Color.White, fontWeight = FontWeight.ExtraBold, fontSize = 21.sp)
                Spacer(Modifier.width(8.dp))
                val newCount = cards.count { isNew(it) }
                if (newCount > 0) {
                    Text(
                        "· $newCount new ${if (newCount == 1) "episode" else "episodes"}",
                        color = Color(0xFF8A8A90),
                        fontSize = 13.5.sp
                    )
                }
            }
        }

        featured?.let { card ->
            item(key = "hero") {
                HeroUpNextCard(
                    card = card,
                    accent = accent,
                    isNewEpisode = isNew(card),
                    onMarkWatched = { onMarkNextEpisodeWatched(card.tvShow.id) },
                    onOpenShow = onTvShowClick,
                )
            }
        }

        if (cards.isNotEmpty()) {
            item { SectionHeader("Continue watching", "${cards.size} shows") }
            item(key = "rail") {
                ContinueWatchingRail(
                    cards = cards,
                    featuredShowId = featuredId,
                    accent = accent,
                    isNewEpisode = isNew,
                    onSelect = { featuredId = it.tvShow.id },
                    onMarkWatched = { onMarkNextEpisodeWatched(it.tvShow.id) },
                )
            }
        }

        if (groupedActivity.isNotEmpty()) {
            item { SectionHeader("Binge Amigos", action = "See all") }
            items(groupedActivity, key = { it.representative.id }) { group ->
                val activity = group.representative
                val isTv = activity.tvShowId != 0
                val ctx = ActivityUserContext(
                    libraryMovie = if (!isTv) uiState.userMovieLibrary[activity.movieId] else null,
                    libraryShow = if (isTv) uiState.userTvLibrary[activity.tvShowId] else null,
                )
                FriendActivityCard(
                    activity = activity,
                    userContext = ctx,
                    reacted = false,
                    reactionCount = 0,
                    commentCount = 0,
                    onClick = onActivityClick,
                    onReact = onReact,
                    onComment = onComment,
                    onAddToList = onAddToList,
                    groupedCount = group.count,
                    rangeLabel = group.rangeLabel,
                )
            }
        }
    }
}

private data class ActivityGroup(
    val representative: UserActivity,
    val count: Int,
    val rangeLabel: String?,
)

private fun groupEpisodeActivity(activities: List<UserActivity>): List<ActivityGroup> {
    val bucketKey = { a: UserActivity -> Triple(a.userId, a.tvShowId, a.timestamp / 86_400_000L) }

    val episodeBuckets = mutableMapOf<Triple<String, Int, Long>, MutableList<UserActivity>>()
    val nonEpisode = mutableListOf<UserActivity>()

    for (activity in activities) {
        if (activity.type == ActivityType.EPISODE_WATCHED.value && activity.tvShowId != 0) {
            episodeBuckets.getOrPut(bucketKey(activity)) { mutableListOf() }.add(activity)
        } else {
            nonEpisode.add(activity)
        }
    }

    val grouped = episodeBuckets.values.map { group ->
        val representative = group.maxBy { it.timestamp }
        if (group.size == 1) {
            ActivityGroup(representative, 1, null)
        } else {
            ActivityGroup(representative, group.size, buildEpisodeRangeLabel(group))
        }
    }

    return (grouped + nonEpisode.map { ActivityGroup(it, 1, null) })
        .sortedByDescending { it.representative.timestamp }
}

private fun buildEpisodeRangeLabel(episodes: List<UserActivity>): String {
    val seasons = episodes.map { it.seasonNumber }.distinct().sorted()
    return if (seasons.size == 1) {
        val epNums = episodes.map { it.episodeNumber }.sorted()
        val s = seasons.first().toString().padStart(2, '0')
        val e1 = epNums.first().toString().padStart(2, '0')
        val e2 = epNums.last().toString().padStart(2, '0')
        "S$s E$e1–E$e2"
    } else {
        val s1 = seasons.first().toString().padStart(2, '0')
        val s2 = seasons.last().toString().padStart(2, '0')
        "S$s1–S$s2"
    }
}

@Composable
private fun SectionHeader(title: String, sub: String? = null, action: String? = null) {
    Row(
        Modifier.fillMaxWidth().padding(horizontal = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(title, color = Color.White, fontWeight = FontWeight.ExtraBold, fontSize = 19.sp)
        if (sub != null) {
            Spacer(Modifier.width(9.dp))
            Text(sub, color = Color(0xFF8A8A90), fontSize = 13.sp)
        }
        Spacer(Modifier.weight(1f))
        if (action != null) {
            Text(action, color = Color(0xFFFFB800), fontWeight = FontWeight.Bold, fontSize = 13.5.sp)
        }
    }
}
